import type { Knot, KnotMode, KnotCategory } from '@shared/knot-schema';

export interface KnotListEntry {
  id: string;
  slug: string;
  name: string;
  category: KnotCategory;
  mode: KnotMode;
  version: number;
  contentHash: string | null;
  publishedAt: string | null;
  updatedAt: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export const api = {
  me: () => request<{ authed: boolean }>('/api/auth/me'),
  login: (password: string) =>
    request<{ ok: true }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  listKnots: () => request<{ knots: KnotListEntry[] }>('/api/knots'),
  getKnot: (id: string) => request<{ knot: Knot }>(`/api/knot/${id}`),
  createKnot: (payload: { name: string; mode: KnotMode; category?: KnotCategory }) =>
    request<{ knot: Knot }>('/api/knots', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  saveKnot: (id: string, draft: unknown) =>
    request<{ knot: Knot }>(`/api/knot/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ draft }),
    }),
  publishKnot: (id: string) =>
    request<{
      ok: true;
      version: number;
      hash: string;
      url: string;
      manifestUrl: string;
      publishedAt: string;
    }>(`/api/knots/${id}/publish`, { method: 'POST' }),
  addFavorite: (slug: string, clientId: string) =>
    request<{ ok: true }>('/api/favorites', {
      method: 'POST',
      body: JSON.stringify({ slug, clientId }),
    }),
  removeFavorite: (slug: string, clientId: string) =>
    request<{ ok: true }>(
      `/api/favorites/${encodeURIComponent(slug)}?clientId=${encodeURIComponent(clientId)}`,
      { method: 'DELETE' },
    ),
  uploadFrames: async (knotId: string, files: File[], fps: number) => {
    const form = new FormData();
    form.set('fps', String(fps));
    for (const f of files) form.append('files', f, f.name);
    const res = await fetch(`/api/knots/${knotId}/frames/upload`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || `Upload failed: ${res.status}`);
    }
    return (await res.json()) as {
      added: number;
      frames: Array<{ id: string; imageUrl: string; durationMs: number; caption?: string }>;
    };
  },
};
